import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Music, Square, Trash2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Empty, fieldCls } from "@/components/dashboard/Shared";

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
const fmtWhen = (iso) => new Date(iso).toLocaleString("es-AR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
const pickMime = () => ["audio/webm", "audio/mp4", "audio/ogg"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

function useRecorder() {
  const rec = useRef(null);
  const started = useRef(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [clip, setClip] = useState(null);
  useEffect(() => {
    if (!recording) return undefined;
    const t = setInterval(() => setElapsed((Date.now() - started.current) / 1000), 250);
    return () => clearInterval(t);
  }, [recording]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = (r.mimeType || mime || "audio/webm").split(";")[0];
        const blob = new Blob(chunks, { type });
        setClip({ file: new File([blob], `voz.${type.split("/")[1]}`, { type }), url: URL.createObjectURL(blob), duration: (Date.now() - started.current) / 1000 });
      };
      rec.current = r;
      started.current = Date.now();
      setClip(null); setElapsed(0); setRecording(true);
      r.start();
    } catch { toast.error("No pudimos acceder al micrófono. Revisa los permisos del navegador."); }
  };
  const stop = () => { rec.current?.stop(); setRecording(false); };
  return { recording, elapsed, clip, setClip, start, stop };
}

const measure = (src) => new Promise((resolve, reject) => {
  const a = new Audio();
  a.onloadedmetadata = () => (isFinite(a.duration) && a.duration > 0 ? resolve(a.duration) : reject());
  a.onerror = reject;
  a.src = src;
});

function Recorder({ show, onSaved }) {
  const api = useApi();
  const { recording, elapsed, clip, setClip, start, stop } = useRecorder();
  const [title, setTitle] = useState("");
  const [position, setPosition] = useState("-1");
  const [busy, setBusy] = useState(false);
  const pickFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const url = URL.createObjectURL(file);
    try { setClip({ file, url, duration: await measure(url) }); } catch { toast.error("No pudimos leer ese audio"); }
  };
  const save = async () => {
    if (clip.duration < 1) { toast.error("El mensaje es demasiado corto"); return; }
    const fd = new FormData();
    fd.append("file", clip.file); fd.append("title", title || "Mensaje del curador");
    fd.append("duration", String(Math.round(clip.duration * 10) / 10)); fd.append("position", position);
    setBusy(true);
    try {
      await api("post", `/curator/shows/${show.id}/messages`, fd);
      toast.success("Mensaje agregado al programa");
      setClip(null); setTitle(""); setPosition("-1"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`recorder-${show.id}`} className="mt-5 space-y-3 rounded-lg border border-sky-400/20 bg-sky-500/5 p-4">
      <p className="text-sm font-semibold text-sky-200">Agregar mensaje de voz</p>
      <div className="flex flex-wrap items-center gap-3">
        {recording ? (
          <Button data-testid={`recorder-stop-${show.id}`} onClick={stop} className="rounded-full bg-red-500 text-white hover:bg-red-600"><Square className="mr-1 h-4 w-4 fill-white" />Detener · {fmtDur(elapsed)}</Button>
        ) : (
          <Button data-testid={`recorder-start-${show.id}`} onClick={start} className="rounded-full bg-sky-500 text-white hover:bg-sky-600"><Mic className="mr-1 h-4 w-4" />{clip ? "Grabar de nuevo" : "Grabar"}</Button>
        )}
        {recording && <span className="flex items-center gap-2 font-mono text-xs text-red-300"><span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />GRABANDO</span>}
        {!recording && (
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-400 hover:text-white">
            <Upload className="h-3.5 w-3.5" />o subir un archivo
            <input data-testid={`recorder-file-${show.id}`} type="file" accept="audio/*" className="hidden" onChange={pickFile} />
          </label>
        )}
      </div>
      {clip && !recording && (
        <div className="space-y-3">
          <audio data-testid={`recorder-preview-${show.id}`} src={clip.url} controls className="h-10 w-full max-w-md" />
          <div className="grid gap-3 md:grid-cols-[2fr_2fr_auto]">
            <Input data-testid={`recorder-title-${show.id}`} placeholder="Título (ej: Presentación)" value={title} onChange={(e) => setTitle(e.target.value)} className={fieldCls} />
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger data-testid={`recorder-position-${show.id}`} className={fieldCls}><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#181824] text-slate-200">
                <SelectItem value="-1">Al final del programa</SelectItem>
                {show.items.map((it, i) => <SelectItem key={i} value={String(i)}>Antes de: {it.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid={`recorder-save-${show.id}`} disabled={busy} onClick={save} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">{busy ? "Guardando..." : "Agregar"}</Button>
          </div>
          <p className="font-mono text-[11px] text-slate-500">Duración: {fmtDur(clip.duration)}</p>
        </div>
      )}
    </div>
  );
}

function ShowBlock({ show, reload }) {
  const api = useApi();
  const { me } = useMe();
  const started = show.start_at <= new Date().toISOString();
  const remove = async (i) => {
    if (!window.confirm("¿Quitar este mensaje?")) return;
    try { await api("delete", `/curator/shows/${show.id}/messages/${i}`); toast.success("Mensaje quitado"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`curator-show-${show.id}`} className="rounded-xl border border-white/10 bg-[#12121A] p-6">
      <p className="font-mono text-[11px] tracking-widest text-amber-500/90">{show.channel_name?.toUpperCase()} · {fmtWhen(show.start_at)}</p>
      <h3 className="mt-1 font-heading text-xl font-bold text-white">{show.title} {started && <span className="ml-1 rounded-full bg-[#FF4500] px-2 py-0.5 font-mono text-[10px] text-white">EN VIVO</span>}</h3>
      <div className="mt-4 space-y-2">
        {show.items.map((it, i) => (
          <div key={`${it.audio_url}-${i}`} className="flex items-center gap-3 rounded-lg bg-[#181824] px-3 py-2 text-sm">
            {it.type === "track" ? <Music className="h-4 w-4 text-[#FF7A45]" /> : <Mic className="h-4 w-4 text-sky-300" />}
            <span className="min-w-0 flex-1 truncate text-white">{it.title}{it.type === "track" && <span className="text-slate-500"> · {it.artist_name}</span>}</span>
            <span className="font-mono text-xs text-slate-500">{fmtDur(it.duration)}</span>
            {!started && it.type === "message" && it.added_by === me?.user_id && (
              <button data-testid={`curator-show-remove-${show.id}-${i}`} onClick={() => remove(i)} className="p-1 text-slate-500 hover:text-red-400" aria-label="Quitar"><Trash2 className="h-3.5 w-3.5" /></button>
            )}
          </div>
        ))}
      </div>
      {started ? <p className="mt-4 text-sm text-slate-500">El programa ya está al aire; no se puede modificar.</p> : <Recorder show={show} onSaved={reload} />}
    </div>
  );
}

export function CuratorShows() {
  const api = useApi();
  const [shows, setShows] = useState(null);
  const reload = () => api("get", "/curator/shows").then(setShows).catch(() => setShows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  if (!shows) return null;
  if (!shows.length) return <Empty>Todavía no tienes programas asignados. El administrador te asigna como presentador.</Empty>;
  return <div data-testid="curator-shows" className="space-y-6">{shows.map((s) => <ShowBlock key={s.id} show={s} reload={reload} />)}</div>;
}
